import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

interface TocItem {
  id: string;
  label: string;
}

const tocItems: TocItem[] = [
  { id: 'intro', label: '1. Introducción' },
  { id: 'datos', label: '2. Datos que Recopilamos' },
  { id: 'uso', label: '3. Uso de la Información' },
  { id: 'compartir', label: '4. Compartir Información' },
  { id: 'cookies', label: '5. Cookies y Tecnologías' },
  { id: 'almacenamiento', label: '6. Almacenamiento y Seguridad' },
  { id: 'derechos', label: '7. Derechos del Usuario' },
  { id: 'menores', label: '8. Privacidad de Menores' },
  { id: 'internacional', label: '9. Transferencia Internacional' },
  { id: 'cambios', label: '10. Cambios a esta Política' },
  { id: 'contacto-p', label: '11. Contacto' },
];

function SectionBlock({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-bold text-foreground-950 mb-4">{title}</h2>
      <div className="text-foreground-700 text-sm leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 pl-5">
      {items.map((item, i) => (
        <li key={i} className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-[0.6em] before:w-1.5 before:h-1.5 before:rounded-full before:bg-secondary-500/60 text-foreground-600">
          {item}
        </li>
      ))}
    </ul>
  );
}

function DataTypeCard({ icon, title, description, items }: { icon: string; title: string; description: string; items: string[] }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center shrink-0">
          <i className={`${icon} text-secondary-600 text-lg`}></i>
        </div>
        <div>
          <h4 className="font-semibold text-foreground-900 text-sm">{title}</h4>
          <p className="text-foreground-500 text-xs">{description}</p>
        </div>
      </div>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="text-xs text-foreground-600 flex items-start gap-2">
            <i className="ri-check-line text-secondary-500 mt-0.5 shrink-0"></i>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PrivacyPage() {
  const [activeSection, setActiveSection] = useState('intro');

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=abstract%20minimalist%20privacy%20shield%20concept%20with%20soft%20geometric%20patterns%2C%20warm%20neutral%20tones%20beige%20and%20cream%2C%20subtle%20paper%20texture%2C%20elegant%20data%20protection%20visual%20metaphor%2C%20clean%20professional%20design%2C%20light%20and%20airy%20atmosphere%2C%20no%20text%20no%20logos%2C%20abstract%20lock%20and%20key%20shapes%20fading%20into%20background&width=1600&height=600&seq=privacy-hero-bg&orientation=landscape)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/95 to-background-50"></div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-5">
            <i className="ri-shield-check-line"></i>
            Privacidad y Datos
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Política de Privacidad</h1>
          <p className="text-foreground-500 text-sm max-w-2xl mx-auto">
            Última actualización: 8 de junio de 2026. Esta política explica cómo recopilamos, usamos y protegemos tu información en RadioFrecuenciaWeb.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="flex-1 pb-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Sidebar TOC */}
            <aside className="lg:w-60 shrink-0">
              <div className="sticky top-24 bg-background-100/80 rounded-2xl border border-background-200/70 p-4">
                <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-widest mb-3">Contenido</p>
                <nav className="space-y-0.5">
                  {tocItems.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveSection(item.id);
                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className={`block px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                        activeSection === item.id
                          ? 'bg-secondary-500 text-background-50'
                          : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60'
                      }`}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0 space-y-10">
              <SectionBlock id="intro" title="1. Introducción">
                <p>
                  En RadioFrecuenciaWeb, la privacidad de nuestros usuarios es una prioridad. Esta Política de
                  Privacidad describe cómo recopilamos, utilizamos, almacenamos y protegemos la información
                  personal que nos proporcionas al utilizar nuestra Plataforma.
                </p>
                <p>
                  Al utilizar RadioFrecuenciaWeb, aceptas las prácticas descritas en esta Política de Privacidad.
                  Si no estás de acuerdo con esta política, te recomendamos no utilizar nuestros servicios.
                </p>
                <p>
                  Cumplimos con las leyes de protección de datos aplicables, incluyendo el Reglamento General de
                  Protección de Datos (GDPR) de la Unión Europea y la Ley de Privacidad del Consumidor de
                  California (CCPA), en la medida que resulten aplicables a nuestras operaciones.
                </p>
              </SectionBlock>

              <SectionBlock id="datos" title="2. Datos que Recopilamos">
                <p>Recopilamos diferentes tipos de información según cómo interactúes con la Plataforma:</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 not-prose">
                  <DataTypeCard
                    icon="ri-user-line"
                    title="Datos de Registro"
                    description="Al crear una cuenta de Radio"
                    items={['Nombre de la radio', 'Correo electrónico', 'Frecuencia RFW', 'Ciudad y ubicación', 'Categoría musical', 'Logo e imagen de perfil']}
                  />
                  <DataTypeCard
                    icon="ri-headphone-line"
                    title="Datos de Oyente"
                    description="Al registrarte como Oyente"
                    items={['Nombre de usuario', 'Correo electrónico', 'Radios favoritas', 'Historial de escucha', 'Preferencias de género']}
                  />
                  <DataTypeCard
                    icon="ri-computer-line"
                    title="Datos Técnicos"
                    description="Automáticamente al navegar"
                    items={['Dirección IP', 'Tipo de navegador', 'Sistema operativo', 'Páginas visitadas', 'Tiempo de sesión']}
                  />
                  <DataTypeCard
                    icon="ri-file-upload-line"
                    title="Contenido Generado"
                    description="Al publicar en la Plataforma"
                    items={['Fotos y videos subidos', 'Mensajes en cabina', 'Transmisiones en vivo', 'Publicaciones y comentarios', 'Archivos de audio']}
                  />
                </div>
              </SectionBlock>

              <SectionBlock id="uso" title="3. Uso de la Información">
                <p>Utilizamos la información recopilada para los siguientes fines:</p>
                <BulletList items={[
                  'Proveer, mantener y mejorar la Plataforma y sus funcionalidades.',
                  'Personalizar la experiencia del usuario, incluyendo recomendaciones de radios basadas en preferencias.',
                  'Procesar registros y administrar cuentas de Radio y Oyente.',
                  'Enviar notificaciones sobre actualizaciones, nuevas funcionalidades y comunicaciones relevantes.',
                  'Monitorear y analizar tendencias de uso para optimizar el rendimiento de la Plataforma.',
                  'Detectar, prevenir y abordar problemas técnicos, fraudes o actividades que violen nuestros Términos.',
                  'Cumplir con obligaciones legales y responder a solicitudes de autoridades competentes.',
                ]} />
              </SectionBlock>

              <SectionBlock id="compartir" title="4. Compartir Información">
                <p>
                  No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto en
                  las siguientes circunstancias limitadas:
                </p>
                <BulletList items={[
                  '<strong>Proveedores de servicios:</strong> Compartimos datos con proveedores que nos ayudan a operar la Plataforma (hosting, análisis, envío de correos), bajo estrictos acuerdos de confidencialidad.',
                  '<strong>Cumplimiento legal:</strong> Podemos divulgar información si así lo requiere la ley, una orden judicial o una autoridad gubernamental.',
                  '<strong>Protección de derechos:</strong> Cuando sea necesario para proteger los derechos, la propiedad o la seguridad de RadioFrecuenciaWeb, nuestros usuarios o el público.',
                  '<strong>Con tu consentimiento:</strong> Compartiremos información con terceros cuando nos des tu consentimiento explícito para hacerlo.',
                ]} />
              </SectionBlock>

              <SectionBlock id="cookies" title="5. Cookies y Tecnologías de Seguimiento">
                <p>
                  Utilizamos cookies y tecnologías similares para mejorar tu experiencia en la Plataforma.
                  Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas
                  nuestro sitio web.
                </p>
                <BulletList items={[
                  '<strong>Cookies esenciales:</strong> Necesarias para el funcionamiento básico de la Plataforma, como mantener tu sesión iniciada y recordar preferencias.',
                  '<strong>Cookies de rendimiento:</strong> Nos ayudan a entender cómo los usuarios interactúan con la Plataforma, permitiéndonos mejorar su funcionamiento.',
                  '<strong>Cookies de funcionalidad:</strong> Permiten recordar tus preferencias, como el idioma o la región, para ofrecer una experiencia personalizada.',
                  '<strong>Cookies de terceros:</strong> Utilizamos servicios de terceros (como Google Analytics) que pueden establecer sus propias cookies. Estas están sujetas a las políticas de privacidad de dichos terceros.',
                ]} />
                <p>
                  Puedes gestionar las preferencias de cookies desde la configuración de tu navegador. Ten en cuenta
                  que deshabilitar ciertas cookies puede afectar la funcionalidad de la Plataforma.
                </p>
              </SectionBlock>

              <SectionBlock id="almacenamiento" title="6. Almacenamiento y Seguridad">
                <p>
                  Implementamos medidas de seguridad técnicas, administrativas y físicas para proteger tu
                  información personal contra accesos no autorizados, alteración, divulgación o destrucción.
                </p>
                <p>
                  Entre las medidas de seguridad que empleamos se incluyen:
                </p>
                <BulletList items={[
                  'Cifrado de datos en tránsito mediante protocolos SSL/TLS.',
                  'Almacenamiento de contraseñas con hash y sal criptográfica (nunca en texto plano).',
                  'Acceso restringido a datos personales solo a personal autorizado y bajo acuerdos de confidencialidad.',
                  'Monitoreo continuo de nuestros sistemas para detectar posibles vulnerabilidades y ataques.',
                  'Copias de seguridad periódicas para garantizar la disponibilidad de los datos.',
                  'Políticas de retención de datos: conservamos tu información solo durante el tiempo necesario para los fines descritos en esta política.',
                ]} />
                <p>
                  Sin embargo, ningún método de transmisión por Internet o almacenamiento electrónico es 100% seguro.
                  No podemos garantizar la seguridad absoluta de tu información.
                </p>
              </SectionBlock>

              <SectionBlock id="derechos" title="7. Derechos del Usuario">
                <p>De acuerdo con las leyes de protección de datos aplicables, tienes los siguientes derechos:</p>
                <BulletList items={[
                  '<strong>Acceso:</strong> Solicitar una copia de los datos personales que tenemos sobre ti.',
                  '<strong>Rectificación:</strong> Corregir datos inexactos o incompletos.',
                  '<strong>Supresión:</strong> Solicitar la eliminación de tus datos personales ("derecho al olvido").',
                  '<strong>Oposición:</strong> Oponerte al procesamiento de tus datos para fines específicos.',
                  '<strong>Portabilidad:</strong> Recibir tus datos en un formato estructurado y transferirlos a otro responsable.',
                  '<strong>Limitación:</strong> Solicitar la restricción del procesamiento de tus datos en determinadas circunstancias.',
                ]} />
                <p>
                  Para ejercer cualquiera de estos derechos, contáctanos a través de privacy@radiofrecuenciaweb.com.
                  Responderemos a tu solicitud en un plazo máximo de 30 días.
                </p>
              </SectionBlock>

              <SectionBlock id="menores" title="8. Privacidad de Menores">
                <p>
                  La Plataforma no está dirigida a menores de 13 años. No recopilamos intencionadamente
                  información personal de menores de 13 años. Si descubrimos que hemos recopilado información
                  personal de un menor de 13 años sin consentimiento parental verificable, eliminaremos dicha
                  información de inmediato.
                </p>
                <p>
                  Si eres padre, madre o tutor y crees que tu hijo nos ha proporcionado información personal,
                  contáctanos para que podamos tomar las medidas necesarias.
                </p>
              </SectionBlock>

              <SectionBlock id="internacional" title="9. Transferencia Internacional de Datos">
                <p>
                  RadioFrecuenciaWeb opera principalmente en Estados Unidos. Si accedes a la Plataforma desde
                  fuera de Estados Unidos, tu información puede ser transferida, almacenada y procesada en
                  Estados Unidos u otros países donde operen nuestros servidores.
                </p>
                <p>
                  Al utilizar la Plataforma, consientes la transferencia de tu información a países que pueden
                  tener leyes de protección de datos diferentes a las de tu país de residencia.
                </p>
              </SectionBlock>

              <SectionBlock id="cambios" title="10. Cambios a esta Política">
                <p>
                  Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios en
                  nuestras prácticas o por razones legales. Publicaremos la versión actualizada en esta página
                  y, cuando los cambios sean significativos, te notificaremos por correo electrónico o mediante
                  un aviso destacado en la Plataforma.
                </p>
                <p>
                  Te recomendamos revisar esta política periódicamente para estar informado sobre cómo
                  protegemos tu información.
                </p>
              </SectionBlock>

              <SectionBlock id="contacto-p" title="11. Contacto">
                <p>Si tienes preguntas, inquietudes o deseas ejercer tus derechos de privacidad, contáctanos:</p>
                <BulletList items={[
                  'Correo electrónico: privacy@radiofrecuenciaweb.com',
                  'Formulario de contacto disponible en la Plataforma.',
                  'Dirección postal: Santa Rosa, California, Estados Unidos.',
                  'Oficial de Protección de Datos: dpo@radiofrecuenciaweb.com',
                ]} />
              </SectionBlock>

              {/* Bottom CTA */}
              <div className="mt-10 p-6 bg-background-100/80 rounded-2xl border border-background-200/70 text-center">
                <p className="text-foreground-700 text-sm mb-3">¿Querés conocer más sobre cómo funciona la Plataforma?</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Link to="/terms" className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-file-text-line mr-1.5"></i>
                    Términos y Condiciones
                  </Link>
                  <Link to="/cookies" className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-file-settings-line mr-1.5"></i>
                    Política de Cookies
                  </Link>
                  <Link to="/radio-rules" className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-radio-line mr-1.5"></i>
                    Reglas para Radios
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}